import React, { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../../../config";

// Payouts existed end to end in the backend -- request, reserve the funds in
// the ledger, transfer, reverse on failure, history -- with no way to reach any
// of it from here. This page showed two balances and an onboarding button, so
// a vendor could watch money accumulate and never withdraw it.
//
// It also used relative URLs, which only resolved through the `proxy` entry in
// package.json. That applies to `npm start` and not to a build, so all three
// calls 404'd against the frontend's own origin once deployed.

function money(value) {
    if (value === null || value === undefined) return "—";
    const n = Number(value);
    return isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

function when(value) {
    if (!value) return "—";
    const d = new Date(value);
    return isNaN(d) ? "—" : d.toLocaleDateString();
}

function statusColour(status) {
    if (status === "paid") return "#2ecc40";
    if (status === "failed") return "#ea476e";
    return "#666";
}

function ManagePayout() {
    const [payout, setPayout] = useState(null);
    const [balances, setBalances] = useState({
        pending_balance: null,
        available_balance: null,
        paid_out_balance: null,
    });
    const [history, setHistory] = useState([]);
    const [amount, setAmount] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const token = localStorage.getItem("token");
    const authHeaders = { Authorization: `Bearer ${token}` };

    const loadBalances = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/balance`, { headers: authHeaders });
            if (!res.ok) throw new Error();
            setBalances(await res.json());
        } catch {
            setBalances({ pending_balance: null, available_balance: null, paid_out_balance: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const loadHistory = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/payouts`, { headers: authHeaders });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setHistory(data.payouts || []);
        } catch {
            setHistory([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        loadBalances();
        loadHistory();
        (async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/vendor/payout-info`, { headers: authHeaders });
                if (!res.ok) throw new Error();
                setPayout(await res.json());
            } catch {
                setPayout(null);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadBalances, loadHistory]);

    async function handleAddPayoutMethod() {
        setError("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/stripe/onboard`, {
                method: "POST",
                headers: authHeaders,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.url) {
                setError(data.message || "Could not start payout setup. Try again shortly.");
                return;
            }
            window.location.href = data.url;
        } catch {
            setError("Network error. Payout setup did not start.");
        }
    }

    async function handleWithdraw(e) {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        setNotice("");
        try {
            // An empty amount withdraws everything that has cleared. The server
            // decides what that is -- sending a number the page computed would
            // race any clearing run that happens in between.
            const body = amount === "" ? {} : { amount };
            const res = await fetch(`${API_BASE_URL}/api/vendor/payouts`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setNotice(`Payout of ${money(data.amount)} started.`);
                setAmount("");
            } else {
                // 400 carries the reason: no connected account, more than has
                // cleared, or below the per-transfer minimum. 502 means the
                // transfer failed at Stripe and the balance is unchanged.
                setError(data.message || "The payout could not be started.");
            }
        } catch {
            setError("Network error. No payout was started.");
        }
        setSubmitting(false);
        loadBalances();
        loadHistory();
    }

    const available = Number(balances.available_balance);
    const canWithdraw = !isNaN(available) && available > 0 && payout && payout.has_payout;

    return (
        <div style={{ maxWidth: 760, margin: "2rem auto", padding: "1rem" }}>
            <h2>Payout &amp; balance</h2>

            {/* --- balances ---------------------------------------------------- */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", margin: "1.5rem 0" }}>
                {[
                    ["Clearing", balances.pending_balance, "Earned, not yet withdrawable"],
                    ["Available", balances.available_balance, "Ready to withdraw"],
                    ["Paid out", balances.paid_out_balance, "Already transferred"],
                ].map(([label, value, hint]) => (
                    <div key={label} style={{
                        flex: "1 1 180px", background: "#fff", borderRadius: 6,
                        padding: "1rem 1.25rem", border: "1px solid #eee",
                    }}>
                        <div style={{ fontSize: "0.8rem", color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            {label}
                        </div>
                        <div style={{ fontSize: "1.6rem", fontWeight: 700, margin: "4px 0" }}>{money(value)}</div>
                        <div style={{ fontSize: "0.82rem", color: "#888" }}>{hint}</div>
                    </div>
                ))}
            </div>

            <p style={{ color: "#666", fontSize: "0.9rem", maxWidth: "58ch" }}>
                Earnings move from <em>clearing</em> to <em>available</em> once the booking has
                finished running and its hold period has passed. The hold exists because a refund
                can reverse an earning, and nothing reverses a bank transfer.
            </p>

            {error && <div style={{ color: "#ea476e", margin: "1rem 0" }}>{error}</div>}
            {notice && !error && <div style={{ color: "#2ecc40", margin: "1rem 0" }}>{notice}</div>}

            {/* --- payout method ------------------------------------------------ */}
            <section style={{ margin: "2rem 0" }}>
                <h3>Payout method</h3>
                {payout && payout.has_payout ? (
                    <p style={{ lineHeight: 1.8 }}>
                        {payout.bank_name || "Bank account"} ••••{payout.last4}<br />
                        Status: {payout.status || "unknown"} · {(payout.currency || "").toUpperCase()} · {payout.country}
                    </p>
                ) : (
                    <p style={{ color: "#666" }}>
                        No payout method on file. You need one before you can withdraw.
                    </p>
                )}
                {/* Primary while there is no method on file -- it is the only
                 * thing a vendor can usefully do on this page until there is
                 * one. Once set up it is a maintenance action and steps back to
                 * secondary, so Withdraw is the single pink thing here. */}
                <button
                    type="button"
                    className={`wb-btn ${payout && payout.has_payout ? "wb-btn--secondary" : "wb-btn--primary"}`}
                    onClick={handleAddPayoutMethod}
                >
                    {payout && payout.has_payout ? "Update payout method" : "Set up payouts"}
                </button>
            </section>

            {/* --- withdraw ------------------------------------------------------ */}
            <section style={{ margin: "2rem 0" }}>
                <h3>Withdraw</h3>
                <form onSubmit={handleWithdraw} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span>$</span>
                    <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder={isNaN(available) ? "" : available.toFixed(2)}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="wb-input"
                        style={{ width: 140 }}
                        aria-label="Amount to withdraw"
                    />
                    <button
                        type="submit"
                        className="wb-btn wb-btn--primary"
                        disabled={submitting || !canWithdraw}
                    >
                        {submitting ? "Starting…" : "Withdraw"}
                    </button>
                    <span style={{ color: "#888", fontSize: "0.88rem" }}>
                        Leave blank to withdraw everything available.
                    </span>
                </form>
                {!canWithdraw && (
                    <div style={{ color: "#888", fontSize: "0.88rem", marginTop: 8 }}>
                        {payout && payout.has_payout
                            ? "Nothing has cleared yet."
                            : "Set up a payout method first."}
                    </div>
                )}
            </section>

            {/* --- history ------------------------------------------------------- */}
            <section style={{ margin: "2rem 0" }}>
                <h3>Payout history</h3>
                {history.length === 0 ? (
                    <div style={{ color: "#666" }}>No payouts yet.</div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", background: "#fff", borderRadius: 6, borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#ea476e22" }}>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Requested</th>
                                    <th style={{ padding: "0.5rem", textAlign: "right" }}>Amount</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Status</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Paid</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((p) => (
                                    <tr key={p.id} style={{ borderBottom: "1px solid #eee" }}>
                                        <td style={{ padding: "0.5rem" }}>{when(p.created_at)}</td>
                                        <td style={{ padding: "0.5rem", textAlign: "right" }}>{money(p.amount)}</td>
                                        <td style={{ padding: "0.5rem", color: statusColour(p.status), fontWeight: 600 }}>
                                            {p.status}
                                        </td>
                                        <td style={{ padding: "0.5rem" }}>{when(p.paid_at)}</td>
                                        <td style={{ padding: "0.5rem", color: "#ea476e" }}>{p.failure_reason || ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

export default ManagePayout;
